import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Icon, Input, Popover } from "@/components/ui";
import { groupTours, startAiTour, TOURS, type AiTour } from "@/help/aiTours";

const COMPLETED_KEY = "aequia:ai-tours-completed";

function readCompleted(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(COMPLETED_KEY) || "[]";
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function matchesQuery(tour: AiTour, query: string): boolean {
  if (!query) return true;
  const haystack =
    `${tour.title} ${tour.subtitle} ${tour.category}`.toLowerCase();
  return haystack.includes(query);
}

export function HelpButton() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [completed, setCompleted] = useState<Set<string>>(() =>
    readCompleted(),
  );
  const navigate = useNavigate();
  const location = useLocation();

  // Refresca el estado de completadas cada vez que se abre el popover.
  useEffect(() => {
    if (open) setCompleted(readCompleted());
  }, [open]);

  const suggestedTour = useMemo(
    () => TOURS.find((tour) => tour.matchPath?.test(location.pathname)),
    [location.pathname],
  );

  const normalizedQuery = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const filtered = TOURS.filter((tour) =>
      matchesQuery(tour, normalizedQuery),
    );
    return groupTours(filtered);
  }, [normalizedQuery]);

  const totalMatches = useMemo(
    () => groups.reduce((sum, group) => sum + group.tours.length, 0),
    [groups],
  );

  function run(tourId: string) {
    setOpen(false);
    startAiTour(tourId, navigate);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative rounded border border-border p-1.5 text-text-2 hover:border-border-strong hover:text-primary"
        aria-label="Ayuda · Guía IA"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-tour-id="help-button"
      >
        <Icon name="help" size={16} />
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        align="end"
        className="flex max-h-[min(560px,calc(100vh-5rem))] w-[min(360px,calc(100vw-1.5rem))] flex-col"
      >
        <div className="shrink-0 border-b border-border p-3">
          <div className="font-serif text-base font-medium text-text">
            Guía interactiva IA
          </div>
          <p className="mt-1 text-xs text-muted">
            Tours guiados de 1-3 minutos por cada módulo de Aequia.
          </p>
          {suggestedTour && (
            <div className="mt-3 rounded-md border border-primary/30 bg-primary-soft/40 p-2">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-eyebrow text-muted">
                Sugerido para esta página
              </div>
              <Button
                variant="primary"
                size="sm"
                icon="play"
                onClick={() => run(suggestedTour.id)}
              >
                {suggestedTour.title}
              </Button>
            </div>
          )}
          <div className="mt-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar guía por nombre o módulo…"
              aria-label="Buscar guía"
              icon="search"
              inputSize="sm"
              hideWrapper
              suffix={
                search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Limpiar búsqueda"
                    className="text-muted hover:text-text"
                  >
                    <Icon name="close" size={14} />
                  </button>
                ) : undefined
              }
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {totalMatches === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-muted">
              No hay guías que coincidan con “{search.trim()}”.
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.category} className="mb-2 last:mb-0">
                <div className="flex items-center gap-2 px-2 pb-1 pt-2">
                  <span className="text-[10px] font-semibold uppercase tracking-eyebrow text-muted">
                    {group.category}
                  </span>
                  {group.core && (
                    <span className="rounded-full bg-primary-soft px-1.5 py-px text-[9px] font-semibold uppercase tracking-eyebrow text-primary-ink">
                      Núcleo
                    </span>
                  )}
                </div>
                <div className="space-y-0.5">
                  {group.tours.map((tour) => {
                    const done = completed.has(tour.id);
                    return (
                      <button
                        key={tour.id}
                        type="button"
                        onClick={() => run(tour.id)}
                        className="flex w-full items-start gap-2 rounded p-2 text-left hover:bg-bg-2"
                      >
                        <Icon
                          name={tour.icon}
                          size={14}
                          className="mt-0.5 shrink-0 text-primary"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="block truncate text-sm font-medium text-text">
                              {tour.title}
                            </span>
                            {done && (
                              <Icon
                                name="check"
                                size={12}
                                className="shrink-0 text-success"
                                aria-label="Completada"
                              />
                            )}
                          </span>
                          <span className="block text-xs text-muted">
                            {tour.subtitle} · {tour.steps} pasos · ~
                            {tour.durationMin} min
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="shrink-0 border-t border-border p-3">
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setOpen(false);
              navigate("/help");
            }}
          >
            Ver todas las guías
          </button>
        </div>
      </Popover>
    </div>
  );
}
